/* ============================================================================
   Furniva — accounts.

   Sign-in by mobile number and a one-time code, and the account panel itself.
   Loaded on every page, because the header's account button opens a sign-in
   dialog from wherever the visitor happens to be.

   HOW THE PIECES FIT

   The panel is a page of this theme (templates/page.account.json), so it
   arrives inside the real header, footer and stylesheets. It has none of the
   customer's data, because Shopify has no session for this account — sign-in
   is by phone, which Shopify's own accounts do not do. The data comes from the
   account Worker in apps/account-worker, which holds the Admin API token and
   returns each view as ready-made markup. There is one implementation of every
   view and it is the Worker's; this file fetches and places it.

   THE TOKEN

   The Worker issues a session token, and it is kept in localStorage and sent
   in an Authorization header. Not a cookie: Shopify strips Set-Cookie from App
   Proxy responses, and a cookie from a separate origin would be a cross-site
   cookie until furniva.in and the Worker were the same site. The honest cost
   is that a script injected into this storefront could read the token. Against
   that, everything stays on furniva.in in the real theme, and the Worker
   answers CORS for this origin alone, so another site cannot read a reply even
   holding a stolen token. See docs/ACCOUNT-PANEL.md.

   Nothing here decides anything. Every response is rendered by the Worker after
   it has checked the token; this file believes what it is told.
   ============================================================================ */
(function () {
  'use strict';

  var TOKEN_KEY = 'furniva:account:token';
  var NAME_KEY = 'furniva:account:name';
  var SAVED_KEY = 'furniva:wishlist:v1'; // owned by furniva-wishlist.js
  var SAVED_SYNC_KEY = 'furniva:wishlist:account-synced';
  var SAVED_PENDING_KEY = 'furniva:wishlist:pending:v1';

  /* Filled from a data attribute on first use, so the theme setting is the one
     place the Worker's address is configured. */
  var ORIGIN = '';

  /* ------------------------------------------------------------- storage -- */
  // Storage throws in private windows and when site data is blocked. Every
  // access is guarded and the feature degrades to "signed out" rather than
  // breaking the page it is on.

  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }
  function writeStore(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (error) {
      /* not remembered; this session still works */
    }
  }

  function token() {
    return readStore(TOKEN_KEY);
  }
  function signedIn() {
    return Boolean(token());
  }

  /* ----------------------------------------------------------------- net -- */

  /* How long any one account request may take before we stop waiting. fetch has
     no deadline of its own, so without this a stalled request leaves a spinner
     turning for as long as the customer is willing to watch it. Generous: a
     cold CRM machine answered a delivery lookup in 21s once, and the Worker's
     own deadline is shorter than this one. */
  var TIMEOUT_MS = 15000;

  function api(path, options) {
    options = options || {};
    if (!ORIGIN) return Promise.reject(new Error('no account origin'));

    var headers = { Accept: 'application/json' };
    if (options.body) headers['Content-Type'] = 'application/json';
    var bearer = token();
    if (bearer) headers.Authorization = 'Bearer ' + bearer;

    // A timeout is reported as a timeout, not as "no connection". They call for
    // different words and a different offer: one is worth retrying, the other
    // means check your signal.
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var expired = false;
    var timer = controller
      ? setTimeout(function () {
          expired = true;
          controller.abort();
        }, options.timeout || TIMEOUT_MS)
      : null;

    return fetch(ORIGIN + path, {
      method: options.body ? 'POST' : 'GET',
      headers: headers,
      // No cookies are involved, in either direction.
      credentials: 'omit',
      mode: 'cors',
      signal: controller ? controller.signal : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined
    }).then(
      function (response) {
        clearTimeout(timer);
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            // The token has been revoked or has expired. Drop it here rather
            // than leaving the panel to fail request after request.
            if (response.status === 401) forget('expired');
            return { status: response.status, data: data || {} };
          });
      },
      function (error) {
        clearTimeout(timer);
        var failure = new Error(expired ? 'account request timed out' : 'account request failed');
        failure.fvTimeout = expired;
        failure.cause = error;
        throw failure;
      }
    );
  }

  /** What to tell someone when a request never completed. */
  function offline(error) {
    return error && error.fvTimeout
      ? 'That took longer than it should have. Try again.'
      : 'No connection. Check your signal and try again.';
  }

  /* Why the last session ended, if it ended in this page's lifetime.

     'expired' means a token was held and the Worker refused it. That happens
     for the ordinary reason — thirty days is up, or somebody signed out on
     another device — and for one that is specific to this architecture:

     Safari deletes script-writable storage, localStorage included, after seven
     days without a visit. The session token lives there, because Shopify
     strips Set-Cookie from App Proxy responses (docs/HANDOVER-CODEX-2.md §2).
     So on an iPhone a customer who does not come back within a week is signed
     out silently, and every account link they follow lands on a sign-in card
     that does not say why.

     Saying why is the whole of the fix available here. The alternative is a
     first-party cookie, which needs furniva.in on Shopify and is not a change
     this file can make. */
  var sessionEnded = null;

  function forget(reason) {
    sessionEnded = reason || null;
    var heldAccountMirror = readStore(SAVED_SYNC_KEY) === '1';
    // Whatever was cached belonged to the session that is ending.
    forgetViews();
    writeStore(TOKEN_KEY, null);
    writeStore(NAME_KEY, null);
    writeStore(SAVED_SYNC_KEY, null);
    writeStore(SAVED_PENDING_KEY, null);
    // A signed-in list can contain items fetched from another device. Do not
    // leave that private account mirror behind after sign-out on a shared one.
    if (heldAccountMirror) replaceLocalSaved([]);
    paintHeader();
  }

  /* -------------------------------------------------------------- pieces -- */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /**
   * Put a control into a working state and back again.
   *
   * The text is written into `[data-fv-btn-label]` when the button has one.
   * Writing the button's own textContent replaces everything inside it, icon
   * included — which is why the sign-out button lost its icon the moment it
   * was pressed.
   */
  function busy(button, on, label) {
    if (!button) return;
    var target = button.querySelector('[data-fv-btn-label]') || button;
    button.disabled = on;
    button.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) {
      button.dataset.fvLabel = target.textContent;
      target.textContent = label || 'Working…';
    } else if (button.dataset.fvLabel) {
      target.textContent = button.dataset.fvLabel;
      delete button.dataset.fvLabel;
    }
  }

  function say(host, kind, message) {
    if (!host) return;
    // Some hosts are drawn hidden; a message nobody can see is no message.
    host.hidden = !message;
    if (!message) {
      host.innerHTML = '';
      return;
    }
    var note = document.createElement('div');
    note.className = 'fv-note fv-note--' + kind;
    note.textContent = message;
    host.innerHTML = '';
    host.appendChild(note);
  }

  /* -------------------------------------------------------------- header -- */

  /**
   * The header is server-rendered and Shopify caches it, so it cannot know who
   * is signed in. Reading localStorage here settles it with no request and no
   * flash of the wrong state.
   */
  /* The name the surfaces outside this file were last painted with, so a
     repaint that changes nothing does not make search pick a new sentence. */
  var paintedName = null;

  function paintHeader() {
    var name = readStore(NAME_KEY) || '';
    var is = signedIn();

    document.documentElement.classList.toggle('fv-signed-in', is);

    $$('[data-fv-account-label]').forEach(function (element) {
      element.textContent = is
        ? name
          ? 'Hello, ' + name
          : 'Your account'
        : element.dataset.fvAccountLabel || 'Sign in';
    });
    $$('[data-fv-when="signed-in"]').forEach(function (element) {
      element.hidden = !is;
    });
    $$('[data-fv-when="signed-out"]').forEach(function (element) {
      element.hidden = is;
    });
    $$('[data-fv-wishlist-link]').forEach(function (link) {
      var guest = link.getAttribute('data-fv-wishlist-guest') || link.getAttribute('href') || '/pages/wishlist';
      if (!link.getAttribute('data-fv-wishlist-guest')) link.setAttribute('data-fv-wishlist-guest', guest);
      link.setAttribute('href', is ? savedItemsUrl() : guest);
    });

    /* The header is not the only thing that greets by name. The search
       greeting is rendered by furniva.js from Liquid's customer.first_name,
       which is always blank here because Shopify holds no session — so it
       reads the stored name instead, and needs telling when it changes.
       Announced rather than called directly: the two files do not import each
       other and only one of them is guaranteed to be on the page. */
    var current = is ? name : '';
    if (current !== paintedName) {
      paintedName = current;
      document.dispatchEvent(new CustomEvent('furniva:account-name', { detail: { name: current } }));
    }

    return is;
  }

  /* --------------------------------------------------------- MSG91 SMS -- */
  /*
     One widget per page, not one per card.

     There are two sign-in cards on /pages/account — the header's dialog and
     the panel's own — and they render the same snippet, so each used to hold
     its own "have I initialised MSG91 yet" flag. But window.sendOtp,
     window.retryOtp and window.verifyOtp are global and belong to whichever
     card called initSendOTP last. A second initialisation therefore rebinds
     them onto a fresh transaction, and the code already sitting on the
     customer's phone belongs to the first one. Verifying it then fails
     against a transaction it was never part of.

     So the bootstrap lives here, at module scope, shared by every card and run
     at most once per page. `identifier` is deliberately not passed to init —
     it is an argument of sendOtp, which is called when the number is known.
  */
  var msg91Script = null;
  var msg91Config = null;
  var msg91Boot = null;

  function msg91Ready() {
    return Boolean(msg91Boot) && typeof window.sendOtp === 'function';
  }

  function loadMsg91Script() {
    if (typeof window.initSendOTP === 'function') return Promise.resolve();
    if (!msg91Script) {
      msg91Script = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = 'https://verify.msg91.com/otp-provider.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = function () {
          msg91Script = null;
          reject(new Error('SMS service did not load'));
        };
        document.head.appendChild(script);
      });
    }
    return msg91Script;
  }

  /* The widget id and token are public browser configuration — they are sent
     to every browser that opens this card. They come from their own endpoint
     rather than from the send, so fetching them early costs the customer none
     of their per-number code budget. */
  function loadMsg91Config() {
    if (!msg91Config) {
      msg91Config = api('/api/otp/sms/config')
        .then(function (result) {
          if (!result.data.ok) throw new Error(result.data.error || 'SMS service is not configured');
          return result.data;
        })
        .catch(function (error) {
          msg91Config = null;
          throw error;
        });
    }
    return msg91Config;
  }

  /**
   * Fetch the script and the configuration, without initialising anything.
   *
   * This is the slow half — a third-party script and a round trip — and it is
   * the half that can be done while the customer is still typing. Nothing is
   * drawn and no challenge is started, so a customer who goes on to use
   * WhatsApp has paid nothing but an idle download.
   */
  function primeMsg91() {
    /* Initialise, not just download. With no captchaRenderId the widget draws
       nothing visible when it starts — exposeMethods hides its popup — so it
       is safe to start it while the number is still being typed, and its
       configuration round trip is then over before "Send by SMS" is pressed.
       A customer who goes on to use WhatsApp has paid for an idle widget. */
    return bootMsg91().catch(function () {
      // Priming is an optimisation. Its failure is the send's problem to
      // report, in the send's own words, if the customer gets that far.
    });
  }

  function bootMsg91() {
    if (msg91Boot) return msg91Boot;
    msg91Boot = Promise.all([loadMsg91Script(), loadMsg91Config()])
      .then(function (results) {
        var config = results[1];
        if (typeof window.initSendOTP !== 'function') throw new Error('SMS service did not load');

        window.initSendOTP({
          widgetId: config.widgetId,
          tokenAuth: config.tokenAuth,
          // `exposeMethods` is what puts sendOtp, retryOtp and verifyOtp on
          // window, and it is also what selects MSG91's invisible hCaptcha
          // path — but only while `captchaRenderId` is absent. See sendSms.
          exposeMethods: true,
          // The SDK validates this config asynchronously and aborts the whole
          // bootstrap if the callbacks are missing, which leaves window.sendOtp
          // undefined for reasons that look nothing like the cause. The real
          // outcomes are handled per call.
          success: function () {},
          failure: function () {}
        });

        /* Ready means configured, not merely present.

           The widget defines window.sendOtp synchronously, the moment it
           starts — before its own configuration request (getWidgetProcess)
           has come back. Until that returns it does not know this account
           requires a security check, so a send made in that window skips the
           check entirely, MSG91 rejects it as "invalid captcha", and the
           customer is told the security check failed. The second attempt then
           works, because by then the configuration has arrived. That was the
           reported first-fails-second-works pattern, to the letter.

           The widget also exposes window.getWidgetData(), which returns that
           configuration once it is in. Waiting for it closes the window. A
           widget build without getWidgetData falls back to the old test,
           rather than never becoming ready. */
        function configured() {
          if (typeof window.sendOtp !== 'function' || typeof window.verifyOtp !== 'function') return false;
          if (typeof window.getWidgetData !== 'function') return true;
          try {
            var data = window.getWidgetData();
            return Boolean(data && (data.widgetId || data.widget_id));
          } catch (error) {
            return false;
          }
        }

        return new Promise(function (resolve, reject) {
          var tries = 0;
          var wait = setInterval(function () {
            tries += 1;
            if (configured()) {
              clearInterval(wait);
              resolve();
            } else if (tries >= 100) {
              clearInterval(wait);
              msg91Boot = null;
              reject(new Error('SMS service did not become ready'));
            }
          }, 100);
        });
      })
      .catch(function (error) {
        msg91Boot = null;
        throw error;
      });
    return msg91Boot;
  }

  /**
   * The access token MSG91 hands back when a code verifies.
   *
   * Its verifyOtp effect calls the success callback with the whole response
   * object — `{ type: 'success', message: <access token> }` — but the SDKs
   * have also handed back the token as a bare string. A string fell through
   * every property read below and left the Worker with no token at all, which
   * it could only report as a failed verification of a correct code.
   */
  function widgetAccessToken(result) {
    if (typeof result === 'string') return result.trim();
    if (!result || typeof result !== 'object') return '';
    var candidates = [result['access-token'], result.accessToken, result.token, result.message];
    for (var i = 0; i < candidates.length; i += 1) {
      var value = candidates[i];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  /* ------------------------------------------------------------- sign in -- */

  /**
   * Wire an OTP card. The dialog in the header and the card on the account
   * page are the same snippet, so one controller drives both.
   *
   * @param {Element} root
   * @param {function} done  called with the verify payload on success
   */
  function wireOtp(root, done) {
    if (!root || root.dataset.fvOtpReady === '1') return;
    root.dataset.fvOtpReady = '1';
    if (root.dataset.fvAccountOrigin) ORIGIN = root.dataset.fvAccountOrigin.replace(/\/+$/, '');

    var phoneForm = $('[data-fv-otp-step="phone"]', root);
    var codeForm = $('[data-fv-otp-step="code"]', root);
    var phoneInput = $('[data-fv-otp-phone]', root);
    var codeInput = $('[data-fv-otp-code]', root);
    var sendButton = $('[data-fv-otp-send]', root);
    var smsButton = $('[data-fv-otp-sms]', root);
    var verifyButton = $('[data-fv-otp-verify]', root);
    var backButton = $('[data-fv-otp-back]', root);
    var resendButton = $('[data-fv-otp-resend]', root);
    var sentTo = $('[data-fv-otp-sent-to]', root);
    var timer = $('[data-fv-otp-timer]', root);
    var feedback = $('[data-fv-otp-feedback]', root);

    var switchButton = $('[data-fv-otp-switch]', root);

    var phone = '';
    var channel = 'whatsapp';
    // MSG91 sends four digits, and the WhatsApp code the Worker mints matches it.
    // MSG91's own widget configuration says otpLength: 4, so the two agree.
    var OTP_LENGTH = 4;
    var countdown = null;

    function step(which) {
      if (phoneForm) phoneForm.hidden = which !== 'phone';
      if (codeForm) codeForm.hidden = which !== 'code';
      say(feedback, 'neutral', '');
      var focus = which === 'phone' ? phoneInput : codeInput;
      if (focus) focus.focus();
    }

    /* Asking again straight away is the commonest way to burn through the
       per-number rate limit, so the button waits and says how long. */
    function hold(seconds) {
      clearInterval(countdown);
      var left = seconds;
      if (resendButton) resendButton.hidden = true;
      function tick() {
        if (left <= 0) {
          clearInterval(countdown);
          if (timer) timer.textContent = '';
          if (resendButton) resendButton.hidden = false;
          return;
        }
        if (timer) timer.textContent = 'You can ask for another in ' + left + 's';
        left -= 1;
      }
      tick();
      countdown = setInterval(tick, 1000);
    }

    /*
       What MSG91 just refused, in terms we can act on.

       Its failure callback hands back an object, not an Error, and its
       `.message` was being shown to customers verbatim — which is how a live
       sign-in screen came to say "invalid captcha" at someone whose code had
       already arrived. Three outcomes need telling apart:

         captcha   the challenge was not drawn or not solved. Recoverable,
                   and the customer has to do something.
         refused   MSG91 took the request and declined it — a bad number, a
                   rate limit, a template problem. Not the customer's to fix.
         outage    we never reached MSG91 at all.
    */
    function classifyMsg91(error) {
      var text = '';
      if (typeof error === 'string') text = error;
      else if (error) text = String(error.message || error.type || error.msg || '');
      var lower = text.toLowerCase();

      if (lower.indexOf('captcha') > -1) return 'captcha';
      if (lower.indexOf('did not load') > -1 || lower.indexOf('did not become ready') > -1) return 'outage';
      if (lower.indexOf('network') > -1 || lower.indexOf('failed to fetch') > -1) return 'outage';
      return 'refused';
    }

    function maskedNumber() {
      return '+91 ' + phone.slice(0, 2) + '••• ••' + phone.slice(-3);
    }

    /** Move to code entry and start the resend clock. One place, both channels. */
    function codeSent(which) {
      channel = which;
      if (sentTo) {
        sentTo.textContent =
          (which === 'sms' ? 'SMS code sent to ' : 'WhatsApp code sent to ') + maskedNumber() + '.';
      }
      if (switchButton) {
        switchButton.hidden = false;
        switchButton.textContent = which === 'sms' ? 'Try WhatsApp instead' : 'Send by SMS instead';
      }
      step('code');
      hold(30);
    }

    /* ------------------------------------------------------------------ *
       The security check, and who does the work

       MSG91's widget configuration for this account has captchaValidations
       on, with hCaptcha as the type, and that stays on: it is the thing
       stopping a script asking for ten thousand codes.

       What changed is who performs it. Passing `captchaRenderId` tells the
       widget to mount a visible hCaptcha checkbox and to send only once its
       own `captchaToken` is set — so every SMS waited for the widget to load,
       then for the customer to tick a box, and only then sent. That is the
       slow path the owner reported, and it is opt-in: MSG91's send path reads

           if (config.exposeMethods && !config.captchaRenderId)
               hcaptchaService.verify() -> processRequestOtp(..., token)

       and that service creates its own container, renders hCaptcha with
       `size: 'invisible'` and calls `hcaptcha.execute()` itself. No host
       element from us, no tick, same protection, same sitekey, and the token
       is still checked by MSG91 server-side before a single SMS goes out.

       So the checkbox is gone and the polling that went with it — we no longer
       read `h-captcha-response` out of the DOM, which the widget never looked
       at anyway. A visitor hCaptcha is unsure about still gets a challenge;
       that is hCaptcha's own overlay and the progress line below says so.
     * ------------------------------------------------------------------ */

    var smsInFlight = false;

    function sendSms(isRetry) {
      if (smsInFlight) return; // one send per press, never two
      smsInFlight = true;
      var button = isRetry ? resendButton : smsButton;
      busy(button, true, 'Sending…');

      // If a challenge does appear, it appears without explanation. Only say
      // so once it has been slow enough that something probably did.
      var challengeHint = setTimeout(function () {
        say(feedback, 'neutral', 'If a security check appears, complete it and your code will send.');
      }, 4000);

      say(feedback, 'neutral', msg91Ready() ? 'Sending your code…' : 'Loading security check…');

      api('/api/otp/sms/start', { body: { phone: phone } })
        .then(function (result) {
          if (!result.data.ok) {
            throw new Error(result.data.error || 'We could not send an SMS code just now.');
          }
          return bootMsg91().then(function () {
            say(feedback, 'neutral', 'Sending your code…');
            return new Promise(function (resolve, reject) {
              // retryOtp is the widget's own resend: it reuses the existing
              // transaction instead of opening a second one, so the code the
              // customer already has is not invalidated by asking again. '11'
              // is SMS, which is the channel this widget's process is on.
              if (isRetry && typeof window.retryOtp === 'function') {
                window.retryOtp('11', resolve, reject);
              } else {
                window.sendOtp(result.data.identifier, resolve, reject);
              }
            });
          });
        })
        .then(function () {
          // Only here. Reaching this point means MSG91 passed its own security
          // check and accepted the send.
          clearTimeout(challengeHint);
          codeSent('sms');
        })
        .catch(function (error) {
          clearTimeout(challengeHint);
          var kind = classifyMsg91(error);
          if (kind === 'captcha') {
            say(feedback, 'error', 'The security check did not pass. Try again, or use WhatsApp.');
          } else if (kind === 'outage') {
            say(feedback, 'error', 'The SMS service did not respond. Try WhatsApp, or try again in a moment.');
          } else {
            say(feedback, 'error', 'We could not send an SMS code to that number. Try WhatsApp instead.');
          }
        })
        .then(function () {
          smsInFlight = false;
          busy(button, false);
        });
    }

    function send(event, requestedChannel) {
      if (event) event.preventDefault();
      var digits = (phoneInput && phoneInput.value ? phoneInput.value : '').replace(/\D/g, '');
      if (digits.length !== 10) {
        say(feedback, 'error', 'Enter your 10-digit mobile number.');
        if (phoneInput) phoneInput.focus();
        return;
      }
      phone = digits;

      if (requestedChannel === 'sms') {
        sendSms(false);
        return;
      }
      channel = 'whatsapp';

      busy(sendButton, true, 'Sending…');
      api('/api/otp/send', { body: { phone: phone } })
        .then(function (result) {
          if (!result.data.ok) {
            say(feedback, 'error', result.data.error || 'We could not send a code just now.');
            return;
          }
          codeSent('whatsapp');
        })
        .catch(function (error) {
          say(feedback, 'error', offline(error));
        })
        .then(function () {
          busy(sendButton, false);
        });
    }

    function acceptVerification(data) {
      if (!data.ok) {
        say(feedback, 'error', data.error || 'That did not work.');
        if (data.expired) step('phone');
        if (codeInput) codeInput.select();
        busy(verifyButton, false);
        return;
      }
      if (data.state !== 'signed-in') {
        busy(verifyButton, false);
        if (done) done(data);
        return;
      }
      // A new session must not be served anything cached under the old one.
      forgetViews();
      writeStore(TOKEN_KEY, data.token);
      writeStore(NAME_KEY, data.name || '');
      // A new session gets one guest-to-account union. Later page loads read
      // the account list instead, so an old browser cache cannot resurrect an
      // item removed on another device.
      writeStore(SAVED_SYNC_KEY, null);
      writeStore(SAVED_PENDING_KEY, null);
      paintHeader();
      mergeSaved().then(function () {
        if (done) done(data);
        else window.location.assign(accountPageUrl());
      });
    }

    function verify(event) {
      if (event) event.preventDefault();
      var code = (codeInput && codeInput.value ? codeInput.value : '').replace(/\D/g, '');
      if (code.length !== OTP_LENGTH) {
        say(feedback, 'error', 'The code is four digits.');
        if (codeInput) codeInput.focus();
        return;
      }

      busy(verifyButton, true, 'Checking…');
      if (channel === 'sms') {
        if (typeof window.verifyOtp !== 'function') {
          say(feedback, 'error', 'SMS verification needs to be restarted.');
          busy(verifyButton, false);
          return;
        }
        window.verifyOtp(
          code,
          function (widgetResult) {
            var accessToken = widgetAccessToken(widgetResult);
            if (!accessToken) {
              // MSG91 verified the code and then gave us nothing to prove it
              // with. Not the customer's mistake, so not their error message —
              // and worded differently from every other SMS failure, because
              // the sentence on the screen is how this gets diagnosed.
              say(feedback, 'error', 'The security check returned nothing to verify. Please use WhatsApp instead.');
              busy(verifyButton, false);
              return;
            }
            api('/api/otp/sms/complete', { body: { phone: phone, accessToken: accessToken } })
              .then(function (result) { acceptVerification(result.data); })
              .catch(function (error) {
                say(feedback, 'error', offline(error));
                busy(verifyButton, false);
              });
          },
          function () {
            say(feedback, 'error', 'That SMS code was not right.');
            if (codeInput) codeInput.select();
            busy(verifyButton, false);
          }
        );
        return;
      }
      api('/api/otp/verify', { body: { phone: phone, code: code } })
        .then(function (result) {
          acceptVerification(result.data);
        })
        .catch(function (error) {
          say(feedback, 'error', offline(error));
          busy(verifyButton, false);
        });
    }

    if (phoneForm) phoneForm.addEventListener('submit', send);
    if (smsButton) smsButton.addEventListener('click', function (event) { send(event, 'sms'); });

    /* Fetch the SMS widget's script and configuration as soon as a complete
       number is in the field — early enough that pressing "Send code by SMS"
       does not begin with a third-party download. Nothing is drawn, no
       challenge starts, and no code budget is spent; a customer who chooses
       WhatsApp has paid an idle download and nothing else. */
    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        if ((phoneInput.value || '').replace(/\D/g, '').length === 10) primeMsg91();
      });
    }
    if (codeForm) codeForm.addEventListener('submit', verify);
    if (backButton)
      backButton.addEventListener('click', function () {
        clearInterval(countdown);
        if (timer) timer.textContent = '';
        if (switchButton) switchButton.hidden = true;
        if (codeInput) codeInput.value = '';
        step('phone');
      });
    if (resendButton) {
      resendButton.addEventListener('click', function (event) {
        if (event) event.preventDefault();
        if (channel === 'sms') sendSms(true);
        else send(event, 'whatsapp');
      });
    }

    // Switching channel from the code screen, for the customer whose WhatsApp
    // code never arrived — the commonest reason anyone abandons sign-in.
    if (switchButton) {
      switchButton.addEventListener('click', function (event) {
        if (event) event.preventDefault();
        if (codeInput) codeInput.value = '';
        if (channel === 'sms') send(event, 'whatsapp');
        else sendSms(false);
      });
    }

    /* A pasted or auto-filled code should just go. */
    if (codeInput) {
      codeInput.addEventListener('input', function () {
        if (codeInput.value.replace(/\D/g, '').length === OTP_LENGTH) verify();
      });
    }
  }

  function accountPageUrl() {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root.replace(/\/$/, '') + '/pages/account';
  }

  /* The page a fresh sign-in lands on (/pages/account?view=welcome): a greeting, a search and a few pieces,
     then "Take me to my account". `next` carries where the customer was
     headed when it was somewhere other than the account page, so the welcome
     page can offer to continue there. Same-origin paths only. */
  function welcomeUrl(next) {
    // An alternate template of the account page (templates/page.welcome.json),
    // chosen with Shopify's own ?view= parameter, so there is no separate page
    // record to keep published, hidden from search, or accidentally delete.
    var url = accountPageUrl() + '?view=welcome';
    if (next && next.charAt(0) === '/' && next.charAt(1) !== '/' && next.split('?')[0] !== accountPageUrl()) {
      url += '&next=' + encodeURIComponent(next);
    }
    return url;
  }

  function savedItemsUrl() {
    return accountPageUrl() + '?tab=saved';
  }

  /* -------------------------------------------------------------- dialog -- */

  function wireDialog() {
    var dialog = $('[data-fv-signin-dialog]');
    if (!dialog) return;

    wireOtp($('[data-fv-otp]', dialog), function (data) {
      if (data.state === 'signed-in') {
        var next = dialog.dataset.fvNext || accountPageUrl();
        window.location.assign(welcomeUrl(next));
        return;
      }
      // No account, or two of them. The account page explains both properly,
      // so hand over rather than reproducing the copy in a dialog.
      window.location.assign(accountPageUrl() + '?state=' + encodeURIComponent(data.state));
    });

    $$('[data-fv-signin-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        // Signed in? The link is a real link to the panel; let it be one.
        if (signedIn()) return;
        event.preventDefault();
        dialog.dataset.fvNext = trigger.getAttribute('data-fv-signin-open') || accountPageUrl();
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });

    $$('[data-fv-signin-close]', dialog).forEach(function (button) {
      button.addEventListener('click', function () {
        dialog.close();
      });
    });

    // Clicking the backdrop closes it, matching the cart drawer.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  /* ------------------------------------------------------------ fetching -- */
  /*
     Three things in front of every view request, because the panel is a set of
     tabs and people use it like one.

     A brief cache. Moving Overview -> Orders -> Overview asked Shopify for the
     same two queries all over again. Thirty seconds is short enough that a
     change made on another device shows up almost at once, and long enough to
     cover flicking between tabs.

     Where it lives matters: this is one customer's orders and addresses, so it
     is held in a variable and nowhere else — no localStorage, no Cache API. It
     dies with the page, it is dropped on any write, and `forget()` empties it
     on sign-out and on a 401, so a second person signing in on the same page
     cannot be served the first one's panel out of it.

     In-flight de-duplication. Two taps on Orders, or a tap arriving with a
     popstate, used to be two identical requests racing to paint one panel.
  */
  var viewCache = {};
  var viewInFlight = {};
  var VIEW_CACHE_MS = 30000;

  function forgetViews() {
    viewCache = {};
  }

  function fetchView(name, reload) {
    var held = viewCache[name];
    if (!reload && held && Date.now() - held.at < VIEW_CACHE_MS) {
      return Promise.resolve({ status: 200, data: held.data, cached: true });
    }
    if (viewInFlight[name]) return viewInFlight[name];

    var request = api('/api/view?name=' + encodeURIComponent(name)).then(
      function (result) {
        delete viewInFlight[name];
        if (result.status === 200 && result.data && result.data.ok) {
          viewCache[name] = { at: Date.now(), data: result.data };
        }
        return result;
      },
      function (error) {
        delete viewInFlight[name];
        throw error;
      }
    );
    viewInFlight[name] = request;
    return request;
  }

  /* ------------------------------------------------------------ sign out -- */
  /*
     One handler, on the document, for every sign-out control there is.

     There are two, and the one a customer actually reaches is the one that was
     dead. The panel's own button, beside the heading, was wired directly at
     page load and worked. The other lives at the bottom of the Profile form —
     which is where anyone goes looking for "Sign out" — and that form is
     markup the Worker renders and the panel drops into `[data-fv-panel-body]`
     on every view change. A listener attached at page load was attached to a
     body that was still empty, and re-rendering replaces the element, so
     nothing was ever bound to it. Pressing it did nothing at all: no request,
     no cleared token, no change of state.

     Delegating to the document fixes both, covers the drawer's control, and
     survives every re-render. It is installed exactly once, and `signingOut`
     stops a second press starting a second round trip.

     `<button>` elements are keyboard-operable already — Enter and Space both
     raise click — so nothing extra is needed for that, and preventDefault
     keeps a control that is a link from navigating first.
  */
  var panelSignedOut = null;
  var signingOut = false;

  /** Shut anything that is only meaningful to someone signed in. */
  function closeAccountUi() {
    var dialog = $('[data-fv-signin-dialog]');
    if (dialog && dialog.open) dialog.close();
    $$('[data-fv-address-dialog], [data-fv-issue-dialog]').forEach(function (element) {
      if (element.open) element.close();
    });
    // The mobile drawer keeps its overlay private to furniva.js. Its own close
    // button is public, and using it keeps the focus and scroll-lock handling
    // that comes with it rather than reproducing that here.
    var drawer = $('[data-fv-nav-drawer]');
    if (drawer && !drawer.hidden) {
      var close = $('[data-fv-nav-close]', drawer) || $('[data-fv-nav-close]');
      if (close) close.click();
    }
  }

  function wireSignOut() {
    document.addEventListener('click', function (event) {
      var control = event.target && event.target.closest
        ? event.target.closest('[data-fv-signout]')
        : null;
      if (!control) return;
      event.preventDefault();
      if (signingOut) return;
      signingOut = true;
      busy(control, true, 'Signing out…');

      // The request revokes the session server-side, so a token copied off a
      // shared machine stops working. Whether it succeeds or not, this browser
      // drops its own copy — a sign-out that fails silently is worse than one
      // that is only local.
      api('/api/signout', { body: {} })
        .catch(function () {})
        .then(function () {
          forget();
          closeAccountUi();
          busy(control, false);
          signingOut = false;

          if (panelSignedOut) {
            panelSignedOut();
            // Drop ?tab= and ?state=: reloading must not ask for a view there
            // is no longer a session to fetch.
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            // Signed out from somewhere else entirely — the drawer, say. The
            // account page is where the signed-out state properly lives.
            window.location.assign(accountPageUrl());
          }
        });
    });
  }

  /* --------------------------------------------------------------- panel -- */

  function wirePanel() {
    var panel = $('[data-fv-panel]');
    if (!panel) return;

    if (panel.dataset.fvAccountOrigin) ORIGIN = panel.dataset.fvAccountOrigin.replace(/\/+$/, '');

    var signedOut = $('[data-fv-panel-signedout]', panel);
    var signedInEl = $('[data-fv-panel-signedin]', panel);
    var body = $('[data-fv-panel-body]', panel);
    var titleEl = $('[data-fv-panel-title]', panel);
    var metaEl = $('[data-fv-panel-meta]', panel);

    function show(which) {
      if (signedOut) signedOut.hidden = which !== 'signedout';
      if (signedInEl) signedInEl.hidden = which !== 'signedin';
      $$('[data-fv-panel-state]', panel).forEach(function (element) {
        element.hidden = element.getAttribute('data-fv-panel-state') !== which;
      });
      if (which === 'signedout') explainEnding();
    }

    /* A sign-in card that appears without explanation, on a page the customer
       reached by tapping "Your orders", reads as the wrong page rather than as
       a sign-in. It is worth one sentence.

       Only when a session actually ended in this page's lifetime. A visitor who
       was never signed in is not told they were signed out. */
    function explainEnding() {
      var host = signedOut && $('[data-fv-otp-feedback]', signedOut);
      if (!host || sessionEnded !== 'expired') return;
      var wanted = new URLSearchParams(window.location.search).get('tab');
      say(
        host,
        'notice',
        wanted
          ? 'You were signed out, so we could not open that. Sign in and we will take you straight there.'
          : 'You were signed out. Sign in to pick up where you left off.'
      );
    }

    // A fresh sign-in is greeted on the welcome page first. Two sign-ins are
    // not arrivals and go straight on: signing back in after a session ran out
    // (explainEnding() promised it), and signing in on the way to a particular
    // view, such as "Your orders" (?tab=orders).
    wireOtp($('[data-fv-otp]', panel), function (data) {
      if (data.state !== 'signed-in') { show(data.state); return; }
      var headed = new URLSearchParams(window.location.search).get('tab');
      if (sessionEnded === 'expired' || headed) load(viewFromUrl());
      else window.location.assign(welcomeUrl());
    });

    $$('[data-fv-panel-retry]', panel).forEach(function (button) {
      button.addEventListener('click', function () {
        show('signedout');
      });
    });

    /* -------------------------------------------------------------- views */

    function viewFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var name = params.get('tab') || 'overview';
      var id = params.get('id');
      if (name === 'order' && id) return 'order:' + id;
      return name;
    }

    var viewGeneration = 0;

    /* A skeleton, but only once the wait is long enough to be worth one. Under
       a quarter of a second, replacing the panel with placeholder lines reads
       as a flicker; past it, leaving the previous view up reads as a dead tap.

       The markup is the snippet the page was rendered with
       (furniva-account-skeleton), kept from before the first view replaced
       it, so the placeholder has one definition. data-fv-skel on the body
       picks its shape. */
    var skeletonHtml = body && body.querySelector('.fv-skeleton') ? body.innerHTML : '';
    function skeleton() {
      return skeletonHtml || (
        '<div class="fv-panel fv-skeleton" aria-hidden="true">' +
        '<span class="fv-skeleton__line" style="width:34%"></span>' +
        '<span class="fv-skeleton__line" style="width:82%"></span>' +
        '<span class="fv-skeleton__line" style="width:64%"></span>' +
        '</div>'
      );
    }
    function skeletonShape(name) {
      return name === 'saved' ? 'grid' : name === 'orders' || name === 'overview' ? 'rows' : 'lines';
    }
    // The section a view belongs to in the nav: an order is under Orders.
    function navKey(name) {
      if (name === 'addresses') return 'profile'; // addresses live under Profile
      return name.indexOf('order:') === 0 ? 'orders' : name;
    }

    /* A freshly arrived view rises in; the class comes off once it has, so a
       later repaint of the same view does not replay it. */
    var enterTimer = null;
    function enter() {
      if (!body || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
      body.classList.remove('is-entering');
      void body.offsetWidth;
      body.classList.add('is-entering');
      clearTimeout(enterTimer);
      enterTimer = setTimeout(function () { body.classList.remove('is-entering'); }, 480);
    }

    var pendingAnchor = '';
    function load(name, push, reload) {
      if (!signedIn()) {
        show('signedout');
        return;
      }
      show('signedin');

      var generation = (viewGeneration += 1);
      var stale = function () { return generation !== viewGeneration; };

      // Where the customer is going is known now, not when the Worker answers:
      // the section is highlighted at the tap.
      paintNav(navKey(name));
      if (body) body.setAttribute('aria-busy', 'true');
      var showing = body && body.querySelector('.fv-skeleton');
      var placeholder = setTimeout(function () {
        if (stale() || !body) return;
        body.setAttribute('data-fv-skel', skeletonShape(name));
        if (!showing) body.innerHTML = skeleton();
        body.classList.add('is-skeleton');
      }, !body || showing || !body.firstElementChild ? 0 : 250);

      function settle() {
        clearTimeout(placeholder);
        if (body) {
          body.setAttribute('aria-busy', 'false');
          body.classList.remove('is-skeleton');
        }
      }

      fetchView(name, reload)
        .then(function (result) {
          if (stale()) return;
          var data = result.data;

          if (result.status === 401 || data.signedIn === false) {
            settle();
            show('signedout');
            return;
          }
          if (!data.ok) {
            settle();
            if (body) {
              body.innerHTML = '';
              say(body, 'error', data.error || 'We could not load that just now.');
            }
            return;
          }

          var fresh = !result.cached || (body && body.querySelector('.fv-skeleton'));
          settle();
          if (body) body.innerHTML = data.html || '';
          if (fresh) enter();
          if (titleEl) titleEl.textContent = data.title || 'Your account';
          if (metaEl) metaEl.textContent = data.meta || '';

          paintNav(data.current, data.counts || {});
          wireFragment();

          // Addresses are a block of Profile now. An old ?tab=addresses link,
          // or "Manage addresses", lands on Profile scrolled to them.
          var anchor = pendingAnchor || (name === 'addresses' ? 'addresses' : '') || (window.location.hash || '').slice(1);
          pendingAnchor = '';
          var target = anchor && body ? body.querySelector('#' + anchor.replace(/[^\w-]/g, '')) : null;
          if (target) {
            setTimeout(function () { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
            push = false;
            window.history.replaceState({ view: 'profile' }, '', '?tab=profile#' + anchor);
          }

          if (push) {
            var query = name.indexOf('order:') === 0
              ? '?tab=order&id=' + encodeURIComponent(name.slice(6))
              : '?tab=' + encodeURIComponent(name);
            window.history.pushState({ view: name }, '', query);
          }
          // Moving between views is a navigation as far as a reader is
          // concerned, so put them at the top of the new one.
          if (push) window.scrollTo({ top: panel.offsetTop - 80, behavior: 'smooth' });
        })
        .catch(function (error) {
          if (stale()) return;
          settle();
          if (!body) return;
          // A retry, not just an apology — and the wording tells a timeout
          // apart from a dead connection, because they call for different acts.
          body.innerHTML = '';
          say(body, 'error', offline(error));
          var again = document.createElement('button');
          again.type = 'button';
          again.className = 'fv-btn fv-btn--secondary fv-btn--sm';
          again.textContent = 'Try again';
          again.addEventListener('click', function () { load(name, false, true); });
          body.appendChild(again);
        });
    }

    function paintNav(current, counts) {
      $$('[data-fv-view-link]', panel).forEach(function (link) {
        var key = link.getAttribute('data-fv-view-link');
        if (key === current) {
          link.setAttribute('aria-current', 'page');
          // The nav is a sideways strip on a phone; keep the current section in it.
          var strip = link.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            var left = link.offsetLeft - strip.offsetLeft - 16;
            if (left < strip.scrollLeft || link.offsetLeft - strip.offsetLeft + link.offsetWidth > strip.scrollLeft + strip.clientWidth) {
              strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
            }
          }
        } else {
          link.removeAttribute('aria-current');
        }
      });
      if (!counts) return;

      $$('[data-fv-nav-count]', panel).forEach(function (span) {
        var value = counts[span.getAttribute('data-fv-nav-count')];
        span.textContent = value > 0 ? String(value) : '';
        span.hidden = !(value > 0);
      });
    }

    /* The "Saved items" badge follows the saved list as it changes — a
       removal here, a heart elsewhere — instead of waiting for the next view
       to load (owner, 2026-10-10: it stayed at the old number until a
       refresh). */
    document.addEventListener('furniva:wishlist', function (event) {
      var items = event.detail && Array.isArray(event.detail.items) ? event.detail.items : null;
      if (!items) return;
      $$('[data-fv-nav-count="saved"]', panel).forEach(function (span) {
        span.textContent = items.length > 0 ? String(items.length) : '';
        span.hidden = !(items.length > 0);
      });
    });

    /* Panel-internal links, in the nav and inside a loaded view alike. */
    panel.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href^="?tab="]') : null;
      if (!link) return;
      event.preventDefault();
      // "?tab=profile#addresses": the part after # is a place in the view.
      var parts = link.getAttribute('href').slice(1).split('#');
      var params = new URLSearchParams(parts[0]);
      var name = params.get('tab');
      var id = params.get('id');
      pendingAnchor = parts[1] || '';
      load(name === 'order' && id ? 'order:' + id : name, true);
    });

    window.addEventListener('popstate', function () {
      load(viewFromUrl());
    });

    /* ------------------------------------------------- inside a loaded view */

    function wireFragment() {
      wireProfile();
      wireAddresses();
      wireSaved();
      wireOrderActions();
      wireWallet();
    }

    /* The wallet: copy the reserved code, or give the coins back. */
    function wireWallet() {
      $$('[data-fv-coins-copy]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          var code = button.getAttribute('data-fv-coins-copy');
          var done = function () {
            var was = button.lastChild && button.lastChild.nodeType === 3 ? button.lastChild : null;
            if (was) { was.textContent = 'Copied'; setTimeout(function () { was.textContent = 'Copy code'; }, 1600); }
          };
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, done);
          else done();
        });
      });
      $$('[data-fv-coins-release]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          busy(button, true, 'Returning…');
          api('/api/coins/release', { body: { code: button.getAttribute('data-fv-coins-release') } })
            .then(function () {
              forgetViews();
              load('wallet', false, true);
              document.dispatchEvent(new CustomEvent('furniva:coins-changed'));
            })
            .catch(function () { busy(button, false); });
        });
      });
    }

    function wireOrderActions() {
      // GST invoice: the tab is opened on the click itself (a tab opened after
      // the request returns is a popup and gets blocked), then filled.
      $$('[data-fv-invoice]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          var orderId = button.getAttribute('data-fv-invoice');
          var tab = window.open('', '_blank');
          if (tab) tab.document.write('<p style="font:15px sans-serif;padding:24px">Preparing your invoice…</p>');
          busy(button, true, 'Preparing…');
          api('/api/orders/invoice', { body: { orderId: orderId } })
            .then(function (result) {
              busy(button, false);
              if (!result.data.ok || !result.data.html) {
                if (tab) tab.close();
                window.alert(result.data.error || 'The invoice could not be prepared.');
                return;
              }
              if (!tab) {
                window.alert('Allow pop-ups for this site to open your invoice.');
                return;
              }
              tab.document.open();
              tab.document.write(result.data.html);
              tab.document.close();
            })
            .catch(function (error) {
              busy(button, false);
              if (tab) tab.close();
              window.alert(offline(error) + ' The invoice could not be prepared.');
            });
        });
      });

      $$('[data-fv-cancel-order]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          if (!window.confirm('Request cancellation for this order? Furniva will verify that it has not been dispatched and confirm the outcome.')) return;
          var orderId = button.getAttribute('data-fv-cancel-order');
          busy(button, true, 'Sending request…');
          api('/api/orders/cancel', { body: { orderId: orderId } })
            .then(function (result) {
              if (!result.data.ok) {
                window.alert(result.data.error || 'The cancellation request could not be recorded.');
                busy(button, false);
                return;
              }
              forgetViews();
              load('order:' + orderId, false, true);
            })
            .catch(function (error) {
              window.alert(offline(error) + ' The cancellation request was not recorded.');
              busy(button, false);
            });
        });
      });

      var dialog = $('[data-fv-issue-dialog]', panel);
      var form = $('[data-fv-issue-form]', panel);
      if (!dialog || !form) return;
      var feedback = $('[data-fv-issue-feedback]', form);
      var submit = $('[data-fv-issue-submit]', form);

      $$('[data-fv-issue-open]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          form.reset();
          say(feedback, 'neutral', '');
          $$('.fv-field, .fv-hint', form).forEach(function (node) { node.hidden = false; });
          $$('.fv-dialog__actions [data-fv-issue-close]', form).forEach(function (button) { button.textContent = 'Not now'; });
          submit.hidden = false;
          busy(submit, false);
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
        });
      });
      $$('[data-fv-issue-close]', dialog).forEach(function (button) {
        button.addEventListener('click', function () { dialog.close(); });
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = new FormData(form);
        busy(submit, true, 'Submitting…');
        say(feedback, 'neutral', '');
        api('/api/orders/issue', {
          body: {
            orderId: data.get('orderId') || '',
            issueType: data.get('issueType') || '',
            description: data.get('description') || ''
          }
        })
          .then(function (result) {
            if (!result.data.ok) {
              say(feedback, 'error', result.data.error || 'The report could not be submitted.');
              busy(submit, false);
              return;
            }
            // The form gives way to the acknowledgement, and "Not now" becomes
            // "Done": what is left to do is close the dialog.
            $$('.fv-field, .fv-hint', form).forEach(function (node) { node.hidden = true; });
            say(feedback, 'success', 'Report received. Your case reference is ' + result.data.reference +
              '. You can follow it on this order.');
            submit.hidden = true;
            $$('.fv-dialog__actions [data-fv-issue-close]', form).forEach(function (button) { button.textContent = 'Done'; });
            // When the dialog closes, the order shows the report's status.
            dialog.addEventListener('close', function reload() {
              dialog.removeEventListener('close', reload);
              forgetViews();
              load('order:' + (data.get('orderId') || ''), false, true);
            });
          })
          .catch(function (error) {
            say(feedback, 'error', offline(error) + ' The report was not submitted.');
            busy(submit, false);
          });
      });
    }

    function wireProfile() {
      var form = $('[data-fv-profile-form]', panel);
      if (!form) return;
      var feedback = $('[data-fv-profile-feedback]', form);
      var save = $('[data-fv-profile-save]', form);

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = new FormData(form);
        busy(save, true, 'Saving…');

        api('/api/profile', {
          body: {
            firstName: data.get('firstName') || '',
            lastName: data.get('lastName') || '',
            email: data.get('email') || '',
            // Marketing only. The order and delivery messages above these are
            // not a choice and are not sent — the Worker refuses to touch them.
            marketingWhatsapp: data.get('marketingWhatsapp') === '1',
            marketingSms: data.get('marketingSms') === '1',
            marketingEmail: data.get('marketingEmail') === '1'
          }
        })
          .then(function (result) {
            if (result.data.ok) {
              // The saved name is the one the header and the panel must show
              // from here on, not the one the session was opened with.
              writeStore(NAME_KEY, result.data.name || '');
              paintHeader();
              // The overview greets by name and the profile form holds these
              // fields. A cached copy of either is now out of date.
              forgetViews();

              if (result.data.partial) {
                // Something did not save. Never report a clean "Saved."
                say(feedback, 'notice', result.data.warning || 'Some changes could not be saved.');
              } else {
                say(feedback, 'success', 'Saved.');
              }
            } else {
              say(feedback, 'error', result.data.error || 'That did not save.');
            }
          })
          .catch(function (error) {
            say(feedback, 'error', offline(error) + ' Your changes were not saved.');
          })
          .then(function () {
            busy(save, false);
          });
      });
    }

    function wireAddresses() {
      var dialog = $('[data-fv-address-dialog]', panel);
      if (!dialog) return;

      var form = $('[data-fv-address-form]', dialog);
      var heading = $('[data-fv-address-heading]', dialog);
      var feedback = $('[data-fv-address-feedback]', dialog);
      var save = $('[data-fv-address-save]', dialog);
      // `company` carries the customer's label for the place — Home, Office —
      // because Shopify has no label field. See the Worker's addresses view.
      var FIELDS = ['firstName', 'lastName', 'company', 'address1', 'address2', 'city', 'province', 'zip', 'phone'];

      var store = {};
      var raw = $('[data-fv-addresses]', panel);
      if (raw) {
        try {
          (JSON.parse(raw.textContent) || []).forEach(function (address) {
            store[address.id] = address;
          });
        } catch (error) {
          store = {};
        }
      }

      function open(address) {
        form.reset();
        heading.textContent = address ? 'Edit address' : 'Add an address';
        say(feedback, 'neutral', '');
        form.elements.id.value = address ? address.id : '';
        FIELDS.forEach(function (name) {
          if (form.elements[name]) form.elements[name].value = address ? address[name] || '' : '';
        });
        if (form.elements.isDefault) form.elements.isDefault.checked = Boolean(address && address.isDefault);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      function write(payload, button) {
        busy(button, true, 'Saving…');
        api('/api/addresses', { body: payload })
          .then(function (result) {
            if (!result.data.ok) {
              say(feedback, 'error', result.data.error || 'That did not save.');
              busy(button, false);
              return;
            }
            // Reload the view rather than patching the DOM: the default flag
            // moves between cards and Shopify may reformat an address on save.
            dialog.close();
            forgetViews();
            pendingAnchor = 'addresses';
            load('profile', false, true);
          })
          .catch(function (error) {
            say(feedback, 'error', offline(error) + ' Nothing was changed.');
            busy(button, false);
          });
      }

      $$('[data-fv-address-new]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          open(null);
        });
      });
      $$('[data-fv-address-edit]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          open(store[button.getAttribute('data-fv-address-edit')]);
        });
      });
      $$('[data-fv-address-default]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          write({ action: 'default', id: button.getAttribute('data-fv-address-default') }, button);
        });
      });
      $$('[data-fv-address-delete]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          if (!window.confirm('Delete this address?')) return;
          write({ action: 'delete', id: button.getAttribute('data-fv-address-delete') }, button);
        });
      });
      $$('[data-fv-address-cancel]', dialog).forEach(function (button) {
        button.addEventListener('click', function () {
          dialog.close();
        });
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = new FormData(form);
        var payload = { action: 'save', id: data.get('id') || '' };
        FIELDS.forEach(function (name) {
          payload[name] = data.get(name) || '';
        });
        payload.isDefault = data.get('isDefault') === '1';
        write(payload, save);
      });

      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    }

    function wireSaved() {
      $$('[data-fv-saved-remove]', panel).forEach(function (button) {
        button.addEventListener('click', function () {
          var handle = button.getAttribute('data-fv-saved-remove');
          var tile = button.closest('[data-fv-saved-tile]');
          busy(button, true, 'Removing…');
          api('/api/saved', { body: { action: 'remove', handle: handle } })
            .then(function (result) {
              if (result.data.ok) {
                replaceLocalSaved(result.data.handles || localSavedHandles().filter(function (item) {
                  return item !== handle;
                }));
                // The tile glides out and the grid closes up; a note confirms
                // it (owner, 2026-10-10). The nav badge follows the
                // furniva:wishlist event replaceLocalSaved raises.
                var leaving = tile && window.FurnivaMotion ? window.FurnivaMotion.leave(tile) : Promise.resolve();
                leaving.then(function () {
                  if (tile) tile.remove();
                  if (window.FurnivaToast) window.FurnivaToast.show('Removed from saved items');
                });
                forgetViews();
              } else busy(button, false);
            })
            .catch(function () {
              busy(button, false);
            });
        });
      });
    }

    /* ---------------------------------------------------------------- start */

    // Sign-out lives on the document, not here — the control the customer is
    // most likely to press is inside a Worker-rendered fragment this panel
    // replaces. These let that one handler drive this panel.
    panelSignedOut = function () {
      show('signedout');
    };

    var state = new URLSearchParams(window.location.search).get('state');
    if (state === 'no-account' || state === 'ambiguous') show(state);
    else if (signedIn()) load(viewFromUrl());
    else show('signedout');
  }

  /* --------------------------------------------------------- saved items -- */

  function localSavedEntries() {
    if (window.FurnivaWishlist && typeof window.FurnivaWishlist.read === 'function') {
      return window.FurnivaWishlist.read();
    }
    try {
      var parsed = JSON.parse(readStore(SAVED_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function localSavedHandles() {
    return localSavedEntries().map(function (entry) {
      return entry && typeof entry === 'object' ? entry.handle : entry;
    }).filter(function (handle) {
      return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,119}$/.test(handle);
    });
  }

  function replaceLocalSaved(handles) {
    if (window.FurnivaWishlist && typeof window.FurnivaWishlist.replaceHandles === 'function') {
      window.FurnivaWishlist.replaceHandles(handles, 'account');
      return;
    }
    var entries = (Array.isArray(handles) ? handles : []).map(function (handle) {
      return { handle: handle, savedAt: Date.now() };
    });
    writeStore(SAVED_KEY, JSON.stringify(entries));
    document.dispatchEvent(new CustomEvent('furniva:wishlist', {
      detail: { items: entries, action: 'replace', source: 'account' }
    }));
  }

  function pendingSaved() {
    try {
      var queue = JSON.parse(readStore(SAVED_PENDING_KEY) || '[]');
      return Array.isArray(queue) ? queue : [];
    } catch (error) {
      return [];
    }
  }

  function queueSaved(action, handle) {
    if (!/^(add|remove)$/.test(action) || !/^[a-z0-9][a-z0-9-]{0,119}$/.test(handle || '')) return;
    var queue = pendingSaved().filter(function (item) { return item.handle !== handle; });
    queue.push({ action: action, handle: handle });
    writeStore(SAVED_PENDING_KEY, JSON.stringify(queue.slice(-200)));
  }

  function withPendingSaved(handles) {
    var next = Array.isArray(handles) ? handles.slice() : [];
    pendingSaved().forEach(function (item) {
      if (item.action === 'add' && !next.includes(item.handle)) next.push(item.handle);
      if (item.action === 'remove') next = next.filter(function (handle) { return handle !== item.handle; });
    });
    return next;
  }

  var savedFlush = null;
  function flushSaved() {
    if (!signedIn()) return Promise.resolve();
    if (savedFlush) return savedFlush;

    function next() {
      var queue = pendingSaved();
      if (!queue.length) return Promise.resolve();
      var item = queue[0];
      return api('/api/saved', { body: item }).then(function (result) {
        if (!result.data.ok) throw new Error(result.data.error || 'saved-items-sync');
        var current = pendingSaved();
        var completed = current.findIndex(function (queued) {
          return queued.action === item.action && queued.handle === item.handle;
        });
        if (completed !== -1) current.splice(completed, 1);
        writeStore(SAVED_PENDING_KEY, JSON.stringify(current));
        if (Array.isArray(result.data.handles)) replaceLocalSaved(withPendingSaved(result.data.handles));
        return next();
      });
    }

    savedFlush = next().catch(function () {
      // Keep the queue. The next page load or wishlist action retries it.
    }).then(function () {
      savedFlush = null;
    });
    return savedFlush;
  }

  /**
   * Fold the guest list into the account. A union, never a replacement: saving
   * three things before signing in must not cost you the four you saved last
   * month, and the other way round.
   *
   * Both sides key on the product handle, which is what the guest list in
   * furniva-wishlist.js has always stored.
   */
  function mergeSaved() {
    var handles = localSavedHandles();
    var request = handles.length
      ? api('/api/saved', { body: { action: 'merge', handles: handles } })
      : api('/api/saved');
    return request.then(function (result) {
      if (result.data.signedIn === false) {
        forget();
        return;
      }
      if (!result.data.ok || !Array.isArray(result.data.handles)) return;
      replaceLocalSaved(result.data.handles);
      writeStore(SAVED_SYNC_KEY, '1');
    }).catch(function () {
      // Sign-in still succeeds. The missing marker makes the next page retry
      // the merge instead of treating the browser cache as canonical.
    });
  }

  function refreshSaved() {
    if (!signedIn()) return Promise.resolve();
    if (readStore(SAVED_SYNC_KEY) !== '1') return mergeSaved();
    return flushSaved().then(function () {
      return api('/api/saved');
    }).then(function (result) {
      if (result.data.signedIn === false) {
        forget();
        return;
      }
      if (result.data.ok && Array.isArray(result.data.handles)) {
        replaceLocalSaved(result.data.handles);
      }
    }).catch(function () {
      // The last confirmed mirror remains usable until connectivity returns.
    });
  }

  function syncSavedChange(event) {
    var change = event.detail || {};
    if (!signedIn() || change.source === 'account') return;
    if (change.action === 'add' || change.action === 'remove') {
      queueSaved(change.action, String(change.handle || ''));
    } else if (change.action === 'prune') {
      (change.handles || []).forEach(function (handle) {
        queueSaved('remove', String(handle || ''));
      });
    } else {
      return;
    }
    flushSaved();
  }

  /* --------------------------------------------------------------- start -- */

  /* `?view=` is reserved by Shopify.

     It selects an alternate template: /pages/account?view=orders asks for a
     template called page.orders, finds none, and renders the plain default
     page template instead — the page's body text, and no account panel. Every
     account link that loaded a page (the mobile drawer, track order, the saved
     items redirect) went there. Links that stayed inside the panel worked,
     because this file intercepted them before any request was made, which is
     why the bug only showed up from outside.

     The parameter is now `tab`. This file loads on every template, including
     the default one those old links land on, so it forwards them: bookmarks,
     emails and cached pages still arrive at the right tab. */
  function forwardLegacyView() {
    var path = window.location.pathname.replace(/\/+$/, '');
    if (!/\/pages\/account$/.test(path)) return false;
    var params = new URLSearchParams(window.location.search);
    if (!params.has('view')) return false;
    // The one alternate template the account page really has.
    if (params.get('view') === 'welcome') return false;
    params.set('tab', params.get('view'));
    params.delete('view');
    window.location.replace(path + '?' + params.toString() + window.location.hash);
    return true;
  }

  function start() {
    if (forwardLegacyView()) return;
    // The session hint set by the inline script in layout/theme.liquid got the
    // page painted in roughly the right state. From here the real token
    // decides, so hand the class over rather than leaving two owners of it.
    document.documentElement.classList.remove('fv-session-pending');
    paintHeader();
    wireSignOut();
    wireDialog();
    wirePanel();

    document.addEventListener('furniva:wishlist', syncSavedChange);
    window.FurnivaAccount = {
      isSignedIn: signedIn,
      request: api,
      clearSession: forget,
      accountUrl: accountPageUrl,
      savedItemsUrl: savedItemsUrl
    };
    document.dispatchEvent(new CustomEvent('furniva:account-ready'));

    // /pages/wishlist remains the useful guest surface. Once signed in, the
    // account view is canonical and the duplicate page is skipped entirely.
    if ($('[data-fv-wishlist-page]') && signedIn()) {
      window.location.replace(savedItemsUrl());
      return;
    }

    if (signedIn()) refreshSaved();

    // Any other sign-in card on the page — the /pages/sign-in section.
    $$('[data-fv-otp]').forEach(function (root) {
      wireOtp(root, function (data) {
        window.location.assign(
          data.state === 'signed-in'
            ? welcomeUrl()
            : accountPageUrl() + '?state=' + encodeURIComponent(data.state)
        );
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
